import { useHistory } from 'react-router-dom';
import {
  Heading,
  Flex,
  Button,
  Icon,
  Text,
  Avatar,
  useToast,
  ButtonGroup,
  HStack,
  VStack,
  Divider,
  useBreakpointValue,
  FlexProps,
  Grid,
  GridItem,
} from '@chakra-ui/react';
import {
  RiBookmarkLine,
  RiCheckboxBlankCircleLine,
  RiCheckboxCircleLine,
  RiDeleteBinLine,
  RiEditLine,
} from 'react-icons/ri';
import { useCallback, useState } from 'react';
import axios from 'axios';
import { LinkButton } from '../../../../../../../components/LinkButton';
import { useActivity } from '../../../../../../../hooks/activity';
import deleteActivitiesService from '../../../../../../../services/Activities/DeleteActivitiesService';
import { translateError } from '../../../../../../../utils/errors';
import { useAuth } from '../../../../../../../hooks/auth';
import { maskMoney } from '../../../../../../../utils/formatters/handleMask';
import { ConfirmationModal } from '../../../../../../../components/ConfirmationModal';
import { SessionBlocksModal } from './components/SessionBlocksModal';

export const ActivityDetailsInfo = (props: FlexProps): JSX.Element => {
  const toast = useToast();

  const { assignment: authenticatedAssignment } = useAuth();

  const { activity } = useActivity();

  const { push } = useHistory();

  const dividerOrientation = useBreakpointValue({
    base: 'horizontal',
    xl: 'vertical',
  });

  const [
    isDeleteConfirmationModalVisible,
    setIsDeleteConfirmationModalVisible,
  ] = useState(false);
  const [isSessionBlocksModalVisible, setIsSessionBlocksModalVisible] =
    useState(false);

  const handleToggleDeleteConfirmationModal = useCallback(() => {
    setIsDeleteConfirmationModalVisible((prevState) => !prevState);
  }, []);

  const handleToggleSessionBlocksModal = useCallback(() => {
    setIsSessionBlocksModalVisible((prevState) => !prevState);
  }, []);

  const handleDeleteActivity = useCallback(async () => {
    if (activity?.id) {
      try {
        await deleteActivitiesService(activity.id);

        toast({
          title: 'Excluído com sucesso',
          description: 'A atividade foi excluída corretamente.',
          status: 'success',
          duration: 3000,
          isClosable: true,
          variant: 'subtle',
          position: 'top-right',
        });

        push('/activities');
      } catch (err) {
        if (axios.isAxiosError(err) && err.response?.status !== 401) {
          handleToggleDeleteConfirmationModal();

          toast({
            title: 'Falha ao excluir',
            description:
              translateError({ message: err.response?.data.message }) ||
              'Ocorreu um erro ao excluir a atividade, tente novamente.',
            status: 'error',
            duration: 3000,
            isClosable: true,
            variant: 'subtle',
            position: 'top-right',
          });
        }
      }
    }
  }, [activity?.id, handleToggleDeleteConfirmationModal, push, toast]);

  return (
    <>
      <ConfirmationModal
        isOpen={isDeleteConfirmationModalVisible}
        onClose={handleToggleDeleteConfirmationModal}
        onConfirm={handleDeleteActivity}
        title="Confirmar exclusão"
        message="Deseja realmente excluir?"
      />

      {activity && (
        <SessionBlocksModal
          activityId={activity.id}
          isOpen={isSessionBlocksModalVisible}
          onClose={handleToggleSessionBlocksModal}
        />
      )}

      <Flex
        direction="column"
        w="100%"
        borderRadius={8}
        bg="white"
        p="8"
        {...props}
      >
        <Flex justify="space-between" align="center">
          <Heading size="lg" fontWeight="normal">
            Detalhes da atividade
          </Heading>
        </Flex>

        {activity && (
          <Flex direction="column" mt="8">
            <Grid
              templateColumns={[
                'repeat(1, 1fr)',
                'repeat(1, 1fr)',
                'repeat(1, 1fr)',
                'repeat(1, 1fr)',
                'repeat(12, 1fr)',
              ]}
              gap="4"
              width="100%"
            >
              <GridItem colSpan={[12, 12, 12, 12, 8]}>
                <Flex direction="column" px="8">
                  <Flex alignItems="flex-start">
                    <Avatar
                      size="2xl"
                      name={activity.name}
                      src={activity.avatarUrl}
                    />

                    <VStack flex="1" alignItems="flex-start" ml="8">
                      <Text fontSize="4xl" fontWeight="bold">
                        {activity.name}
                      </Text>

                      <HStack>
                        <Text>Local:</Text>
                        <Text color="gray.500">{activity.spot.name}</Text>
                      </HStack>

                      <HStack>
                        <Text>Empreendimento:</Text>
                        <Text color="gray.500">
                          {activity.spot.venture.name}
                        </Text>
                      </HStack>

                      {!!activity.bookingPrice && (
                        <HStack>
                          <Text>Valor da reserva:</Text>
                          <Text color="gray.500">
                            {maskMoney(activity.bookingPrice)}
                          </Text>
                        </HStack>
                      )}

                      <HStack>
                        <Text>Ativo:</Text>
                        {activity.isActive ? (
                          <Icon
                            fontSize="3xl"
                            as={RiCheckboxCircleLine}
                            color="green"
                          />
                        ) : (
                          <Icon
                            fontSize="3xl"
                            as={RiCheckboxBlankCircleLine}
                            color="gray.400"
                          />
                        )}
                      </HStack>
                    </VStack>

                    <Divider
                      hidden={dividerOrientation === 'horizontal'}
                      ml="4"
                      alignSelf="center"
                      height="80%"
                      borderColor="gray.300"
                      orientation="vertical"
                    />
                  </Flex>

                  <ButtonGroup mt="8">
                    {authenticatedAssignment?.featureGroup.features.some(
                      (feature) =>
                        [
                          'ACTIVITY_FULL_ACCESS',
                          'ACTIVITY_WRITE_ACCESS',
                        ].includes(feature.key),
                    ) && (
                      <LinkButton
                        colorScheme="yellow"
                        color="white"
                        icon={<Icon as={RiEditLine} fontSize="16" />}
                        to={{
                          pathname: '/activities/update',
                          state: {
                            activityId: activity.id,
                          },
                        }}
                      >
                        Editar
                      </LinkButton>
                    )}

                    {/* {authenticatedAssignment?.featureGroup.features.some(
                      (feature) =>
                        [
                          'ACTIVITY_SCHEDULE_FULL_ACCESS',
                          'ACTIVITY_SCHEDULE_WRITE_ACCESS',
                        ].includes(feature.key),
                    ) && (
                      <Button
                        colorScheme="blue"
                        leftIcon={<Icon as={RiPauseCircleLine} fontSize="20" />}
                        onClick={handleToggleSessionBlocksModal}
                        size="sm"
                        fontSize="sm"
                      >
                        Gerenciar paralisações
                      </Button>
                    )} */}

                    {authenticatedAssignment?.featureGroup.features.some(
                      (feature) =>
                        [
                          'ACTIVITY_FULL_ACCESS',
                          'ACTIVITY_DELETE_ACCESS',
                        ].includes(feature.key),
                    ) && (
                      <Button
                        size="sm"
                        fontSize="sm"
                        colorScheme="red"
                        onClick={handleToggleDeleteConfirmationModal}
                        leftIcon={<Icon as={RiDeleteBinLine} fontSize="20" />}
                      >
                        Excluir
                      </Button>
                    )}
                  </ButtonGroup>
                </Flex>
              </GridItem>

              {activity.description && (
                <GridItem colSpan={[12, 12, 12, 12, 4]}>
                  <Flex height="100%">
                    <Divider
                      hidden={dividerOrientation === 'horizontal'}
                      px="2"
                      height="80%"
                      borderColor="gray.300"
                      orientation="vertical"
                    />

                    <Flex flexDirection="column" w="100%">
                      <Divider
                        hidden={dividerOrientation === 'vertical'}
                        borderColor="gray.300"
                        mt="4"
                        mb="8"
                      />
                      <Flex
                        flexDirection="column"
                        px={[8, 8, 8, 8, 0]}
                        w="100%"
                      >
                        <HStack mt={[0, 0, 0, 0, 2]}>
                          <Icon as={RiBookmarkLine} fontSize="20" />
                          <Text>Descrição</Text>
                        </HStack>

                        <Text
                          fontSize="sm"
                          color="gray.500"
                          mt="4"
                          px={[4, 4, 4, 4, 0]}
                        >
                          {activity.description}
                        </Text>
                      </Flex>
                    </Flex>
                  </Flex>
                </GridItem>
              )}
            </Grid>
          </Flex>
        )}
      </Flex>
    </>
  );
};
